import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { Recipe } from '@kemu-io/kemu-core/types';
import { RecipeType } from '@kemu-io/kemu-types';
import { NavigateFunction } from 'react-router-dom';
import { InterfaceState } from '../interfaceSlice';
import { setRecipeType, setRecipeMetaAction } from '../../Workspace/workspaceSlice';
import recipeApi from '@src/api/recipe/recipeApi';
import { openRecipeAction } from '@src/app/reducers/workspace/openRecipeReducer';
import routes from '@src/common/routes/index';
import { AsyncRequestStatus } from '@src/types/core_t';
import { UseAlertInstance } from '@components/alert/useAlert';
import type { NavigateWithSearch } from '@common/hooks/useNavigateWithSearch';
import { safeJsonParse } from '@common/utils';
/**
 * Downloads the full contents of a recipe and its storage
 * triggering notification events.
 */
export const downloadRecipeAction = createAsyncThunk<void, {
  recipeId: string, 
  navigate: NavigateFunction | NavigateWithSearch, 
  alert: UseAlertInstance, 
  version?: number 
}>(
  '/Interface/downloadRecipe', async (options, thunkAPI) => {
    try {
      // Get Signed URLs
      const {contents, metadata} = await recipeApi.getRecipeById({ id: options.recipeId, version: options.version });
      const recipe = safeJsonParse<Recipe>(contents);
      if (!recipe) {
        throw new Error(`Recipe file is corrupted and cannot be opened`);
      }

      // recipe.id = options.recipeId;

      // Make sure the name displayed comes from the db entity (very important for cases when the recipe
      // was cloned, then the name in the contents will be the old recipe's name);
      recipe.name = metadata.name;
      recipe.uuid = metadata.uuid;
      // const recipeCombinedDisk = <ArrayBuffer> results[1];
      // Parse the recipe contents
      // const recipe: Recipe = JSON.parse(recipeJSON);
      // If storage is available, split it into storage units.
      // if (recipeCombinedDisk) {
      //   // TODO: Checksum?
      //   recipe.storage = storageDiskToMap(recipeCombinedDisk);
      // }

      // Clear any previous download indicator
      // thunkAPI.dispatch(clearInstallationProgress('all'));

      // Clear the bundle path that might be part of the recipe (older recipes, 
      // bundle is as of 01/Nov/2021 actively removed during saving), 
      // to force using a version pointed by either the cache or the dev blocks.
      const recipeThingsIds = Object.keys(recipe.blocks);

      recipeThingsIds.forEach(blockKey => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (recipe.blocks[blockKey] as any).bundle;
      });

      // Recipes might have been created with older version of Things
      // here we find the latest installed version and replace the details of
      // the thing in the recipe.
      // for (const thingId of recipeThingsIds) {
      //   const thing = recipe.blocks[thingId];
      //   const compatibleVersion = await getCompatibleVersionInstalled(thing.id, thing.version);
      //   thing.version = compatibleVersion;
      // }

      // Check blocks in dev mode from this recipe
      // overrideDevThings(recipe.blocks);

      // eww, "as unknown as Record"? ugly as...
      // const installationResults = await downloadThingBundlesInParallel(recipe.blocks as unknown as Record<string, LimitedThingInfo>, (blockRecipeId, download) => {
      //   if (!download.errorMsg) {
      //     thunkAPI.dispatch(setInstallationProgress({
      //       id: recipe.blocks[blockRecipeId].id,
      //       block: recipe.blocks[blockRecipeId] as unknown as LimitedThingInfo,
      //       progress: download.progress!,
      //       version: recipe.blocks[blockRecipeId].version,
      //     }));
      //   } else if (download.errorMsg) {
      //     thunkAPI.dispatch(setInstallationProgress({
      //       id: recipe.blocks[blockRecipeId].id,
      //       block: recipe.blocks[blockRecipeId] as unknown as LimitedThingInfo,
      //       errorMsg: download.errorMsg,
      //       version: recipe.blocks[blockRecipeId].version,
      //     }));
      //   }
      // });

      // const successfullyInstalled = [];
      // installationResults.forEach(result => {
      //   if (result.status === 'fulfilled') {
      //     // Clear out the progress of those that downloaded OK
      //     thunkAPI.dispatch(clearInstallationProgress({
      //       id: result.value.id,
      //       version: result.value.version,
      //     }));
      //     successfullyInstalled.push(recipe.blocks[result.value.id]);
      //   }
      // });



      // Make sure ALL blocks were successfully downloaded
      // if (successfullyInstalled.length === Object.keys(recipe.blocks).length) {

        // if (recipeInfo.linkedTutorial) {
        //   thunkAPI.dispatch(setActiveTutorialInfo({
        //     id: recipeInfo.linkedTutorial,
        //     isDraft: false,
        //     notificationShown: false,
        //     step: 0,
        //   }));
        // } else {
        //   // Clear any reference of a previous tutorial
        //   thunkAPI.dispatch(setActiveTutorialInfo(null));
        // }

        // Registers the recipe with Kemu Core and initialize it.
        await thunkAPI.dispatch(
          openRecipeAction({
            recipe: recipe,
            isDefault: false,
            dbInfo: {
              id: metadata.dbId,
              authorId: metadata.author.id,
              version: metadata.version,
              recipeType: metadata.cloud ? RecipeType.Cloud : RecipeType.Browser,
            }
          })
        );

        // After recipe has been registered, persist version info into workspace state via meta action
        thunkAPI.dispatch(setRecipeMetaAction({
          dbId: metadata.dbId,
          version: metadata.version,
          name: metadata.name,
          uuid: recipe.uuid,
          moreVersionsAvailable: !!metadata.moreVersionsAvailable,
          recipeType: metadata.cloud ? RecipeType.Cloud : RecipeType.Browser,
          versionHistory: metadata.versionHistory || [],
        }));

        // Navigate back to app depending on the type of recipe
        if (metadata.cloud) {
          thunkAPI.dispatch(setRecipeType({
            type: RecipeType.Cloud,
            cloudInfo: metadata.cloud,
          }));

          options.navigate(routes.recipe.getCloudRecipeRoute(metadata.dbId));
        } else {
          thunkAPI.dispatch(setRecipeType({
            type: RecipeType.Browser,
            cloudInfo: null
          }));

          options.navigate(routes.recipe.getBrowserRecipeRoute(metadata.dbId));
        }
      // } else {
      //   // console.log('Cannot open recipe, some blocks are not currently installed');
      //   throw new Error(`Some blocks are not currently installed`);
      // }

    } catch (e) {
      console.error('Error downloading recipe: ', e);
      throw e;
    }
  }
);

export const downloadRecipeActionReducer = ((builder: ActionReducerMapBuilder<InterfaceState>): void => {
  builder.addCase(downloadRecipeAction.pending, (state) => {
    state.recipeDownloadDetails = {
      ...state.recipeDownloadDetails,
      contentProgress: 0,
      storageProgress: 0,
      noProgress: true,
      status: AsyncRequestStatus.loading
    };
  });

  builder.addCase(downloadRecipeAction.fulfilled, (state) => {
    state.recipeDownloadDetails = {
      ...state.recipeDownloadDetails,
      status: AsyncRequestStatus.completed,
      error: undefined
    };
  });

  builder.addCase(downloadRecipeAction.rejected, (state, action) => {
    state.recipeDownloadDetails = {
      ...state.recipeDownloadDetails,
      status: AsyncRequestStatus.error,
      error: action.error
    };
  });
});
